import { useState, useEffect } from "react"
import { CircleNotch } from "@phosphor-icons/react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../../components/ui/dialog"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Textarea } from "../../../components/ui/textarea"
import { Badge } from "../../../components/ui/badge"
import { Checkbox } from "../../../components/ui/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select"
import { MatterSearch } from "../../../components/ui/collection-search/matter-search"
import { UserSearch } from "../../../components/ui/collection-search"
import { DatePicker } from "../../../components/ui/date-picker"
import { ObjectName } from "../../../components/ui/object-name"
import { useUIStore } from "../../../stores/ui-store"
import { useAuthStore } from "../../../stores/auth-store"
import { useBillingCategories } from "../hooks/useBillingCategories"
import { useTemplatesByCategory } from "../hooks/useTimeEntryTemplates"
import { useCreateTimeEntry, useUpdateTimeEntry, useApproveTimeEntries } from "../hooks/useTimeEntries"
import { useTimerStore } from "../stores/timer-store"
import { todayStr } from "../utils"
import { toast } from "sonner"
import type {
  CreateTimeEntryRequest,
  TimeEntry,
} from "../types"
import { SOURCE_LABELS } from "../types"

interface TimeEntryFormInitialData {
  matter?: string
  description?: string
  date?: string
}

interface TimeEntryFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  matterId?: string | null
  editEntry?: TimeEntry | null
  initialData?: TimeEntryFormInitialData | null
  onSuccess?: () => void
}

interface FormState {
  matter: string
  user: string
  billingCategory: string
  templateId: string
  description: string
  date: string
  durationMinutes: string
  billable: boolean
  source: "manual" | "timer"
}

export function TimeEntryForm({
  open,
  onOpenChange,
  matterId,
  editEntry,
  initialData,
  onSuccess,
}: TimeEntryFormProps) {
  const selectedDivision = useUIStore((s) => s.selectedDivision)
  const currentUser = useAuthStore((s) => s.user)
  const createMutation = useCreateTimeEntry()
  const updateMutation = useUpdateTimeEntry(matterId || editEntry?.matter)
  const approveMutation = useApproveTimeEntries(matterId || editEntry?.matter)

  const makeDefaultForm = (): FormState => ({
    matter: "",
    user: currentUser?._id || "",
    billingCategory: "",
    templateId: "",
    description: "",
    date: todayStr(),
    durationMinutes: "",
    billable: true,
    source: "manual",
  })

  const [form, setForm] = useState<FormState>(makeDefaultForm)
  const { data: categories } = useBillingCategories(selectedDivision || undefined)
  const { data: templates } = useTemplatesByCategory(
    selectedDivision || undefined,
    form.billingCategory || undefined
  )

  // Computed values
  const durationNum = parseFloat(form.durationMinutes) || 0

  // Initialize form from edit entry, timer data, or defaults
  // Read stoppedTimerData imperatively to avoid double-fire when it's cleared
  useEffect(() => {
    if (!open) return

    if (editEntry) {
      setForm({
        matter: editEntry.matter,
        user: editEntry.user || currentUser?._id || "",
        billingCategory: editEntry.billing_category || "",
        templateId: editEntry.time_entry_template || "",
        description: editEntry.description || "",
        date: editEntry.date || todayStr(),
        durationMinutes: String(editEntry.duration_minutes || ""),
        billable: editEntry.billable !== false,
        source: "manual",
      })
    } else if (initialData) {
      setForm({
        ...makeDefaultForm(),
        matter: initialData.matter || matterId || "",
        description: initialData.description || "",
        date: initialData.date || todayStr(),
      })
    } else {
      const timerData = useTimerStore.getState().stoppedTimerData
      if (timerData) {
        setForm({
          ...makeDefaultForm(),
          matter: timerData.matter,
          description: timerData.description,
          billingCategory: timerData.category || "",
          templateId: timerData.template || "",
          durationMinutes: String(timerData.durationMinutes),
          date: todayStr(),
          source: "timer",
        })
        useTimerStore.getState().setStoppedTimerData(null)
      } else {
        setForm({
          ...makeDefaultForm(),
          matter: matterId || "",
          date: todayStr(),
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editEntry, matterId, initialData])

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }))
  }

  // When template selected, auto-fill description + billable
  const handleTemplateChange = (id: string) => {
    updateField("templateId", id)
    if (id && templates) {
      const tmpl = templates.find((t) => t._id === id)
      if (tmpl) {
        if (!form.description) updateField("description", tmpl.description || tmpl.name)
        updateField("billable", tmpl.billable !== false)
        if (tmpl.default_duration) updateField("durationMinutes", String(tmpl.default_duration))
      }
    }
  }

  // Returns the entry ID on success, null on failure
  const doSave = async (): Promise<string | null> => {
    if (!form.matter) {
      toast.error("Please select a matter")
      return null
    }
    if (durationNum <= 0) {
      toast.error("Duration must be greater than 0")
      return null
    }

    try {
      if (editEntry) {
        await updateMutation.mutateAsync({
          id: editEntry._id,
          data: {
            description: form.description.trim(),
            date: form.date,
            duration_minutes: durationNum,
            billable: form.billable,
            billing_category: form.billingCategory || undefined,
            time_entry_template: form.templateId || undefined,
            user: form.user || undefined,
          },
        })
        return editEntry._id
      } else {
        const data: CreateTimeEntryRequest = {
          matter: form.matter,
          description: form.description.trim() || undefined,
          date: form.date,
          duration_minutes: durationNum,
          billable: form.billable,
          billing_category: form.billingCategory || undefined,
          time_entry_template: form.templateId || undefined,
          source: form.source,
          user: form.user || undefined,
        }
        const result = await createMutation.mutateAsync(data)
        return result?.data?._id || null
      }
    } catch {
      return null
    }
  }

  const handleSave = async () => {
    const id = await doSave()
    if (!id) return
    toast.success(isEdit ? "Time entry updated" : "Time entry created")
    onSuccess?.()
    onOpenChange(false)
  }

  const handleSaveAndApprove = async () => {
    const id = await doSave()
    if (!id) return
    try {
      await approveMutation.mutateAsync({ time_entry_ids: [id] })
      toast.success(isEdit ? "Entry saved & approved" : "Entry created & approved")
      onSuccess?.()
      onOpenChange(false)
    } catch {
      // Approve failed but save succeeded
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending || approveMutation.isPending
  const isEdit = !!editEntry

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Time Entry" : "Create Time Entry"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update time entry details."
              : "Log time for a matter."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Source badge for edit mode */}
          {editEntry?.source && (
            <Badge variant="secondary">
              Source: {SOURCE_LABELS[editEntry.source]}
            </Badge>
          )}

          {/* Matter */}
          <div className="space-y-2">
            <Label>Matter *</Label>
            {isEdit || form.source === "timer" || matterId ? (
              <div className="flex h-9 w-full items-center rounded-md border bg-muted px-3 text-sm">
                <ObjectName collection="matters" id={form.matter} />
              </div>
            ) : (
              <MatterSearch
                value={form.matter || null}
                onChange={(opt) => updateField("matter", opt?.value || "")}
                placeholder="Search matters..."
              />
            )}
          </div>

          {/* Category + Template (two-step selection) */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={form.billingCategory || "none"}
                onValueChange={(v) => {
                  updateField("billingCategory", v === "none" ? "" : v)
                  updateField("templateId", "")
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {(categories || []).map((cat) => (
                    <SelectItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Activity *</Label>
              <Select
                value={form.templateId || "none"}
                onValueChange={(v) =>
                  handleTemplateChange(v === "none" ? "" : v)
                }
                disabled={!form.billingCategory || !templates?.length}
              >
                <SelectTrigger className="truncate">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
                  <SelectItem value="none">None</SelectItem>
                  {(templates || []).map((tmpl) => (
                    <SelectItem key={tmpl._id} value={tmpl._id} title={tmpl.name}>
                      <span className="truncate">{tmpl.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder="Describe the work performed..."
              rows={3}
            />
          </div>

          {/* User */}
          <div className="space-y-2">
            <Label>User</Label>
            <UserSearch
              value={form.user || null}
              onChange={(opt) => updateField("user", opt?.value || "")}
              placeholder="Select user..."
            />
          </div>

          {/* Date + Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <DatePicker
                value={form.date ? new Date(form.date + "T00:00:00") : undefined}
                onChange={(date) => updateField("date", date ? date.toISOString().split("T")[0] : todayStr())}
                placeholder="Select date"
              />
            </div>
            <div className="space-y-2">
              <Label>Duration (minutes) *</Label>
              <Input
                type="number"
                value={form.durationMinutes}
                onChange={(e) => updateField("durationMinutes", e.target.value)}
                placeholder="e.g. 30"
                min={1}
              />
            </div>
          </div>

          {/* Billable */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="billable"
              checked={form.billable}
              onCheckedChange={(checked) =>
                updateField("billable", checked === true)
              }
            />
            <Label htmlFor="billable" className="cursor-pointer">
              Billable
            </Label>
            <span className="text-xs text-muted-foreground ml-2">
              Non-billable time is tracked but not invoiced
            </span>
          </div>

        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isPending || !form.matter}>
            {isPending && (
              <CircleNotch className="mr-2 h-4 w-4 animate-spin" />
            )}
            {isEdit ? "Save Changes" : "Create Entry"}
          </Button>
          {(!isEdit || editEntry?.status === "draft") && (
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={handleSaveAndApprove}
              disabled={isPending || !form.matter || !form.billingCategory || !form.templateId || durationNum <= 0}
            >
              {isPending && (
                <CircleNotch className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isEdit ? "Save & Approve" : "Create & Approve"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
